import React from 'react';
import produce from 'immer';

import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {useTranslation} from 'common/i18n/hooks';
import {MultiAttributionType} from 'common/types/reports/bookmark';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {
  getMultiAttributionFromEntry,
  isSupportedMultiAttributionTypeForEntry,
} from 'common/query-builder/query-builder/util';
import {
  getMultiAttributionLabel,
  getMultiAttributionTitle,
} from 'common/query-builder/query-entry-section/util/multi-attribution';
import {DisplaySize} from 'common/components/tooltip/types';
import {MultiAttributionMenu} from 'common/widgets/multi-attribution-menu';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import type {RowProps} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';

export function MultiAttributionMeasurement(props: RowProps & {extraLabel?: string}) {
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {print} = useTranslation();

  const [isMultiAttributionOpen, setIsMultiAttributionOpen] = React.useState(false);
  const multiAttribution = getMultiAttributionFromEntry(props.entry);

  const handleUpdateMultiAttribution = (updatedMultiAttribution: MultiAttribution | null) => {
    const updatedEntry = produce(props.entry, (entry) => {
      entry.measurement.multiAttribution = updatedMultiAttribution;
    });
    props.updateEntry(updatedEntry);
    setIsMultiAttributionOpen(false);
  };

  if (!props.sectionProps.canUseMultiAttribution || !multiAttribution) {
    return null;
  }

  // limits certain attribution types for Funnels and Retention metrics
  const isRetentionOrFunnelBehavior =
    props.entry.type !== BookmarkTypes.TopLevelMetricType.NewFormulaEntry &&
    (props.entry.behavior.type === BookmarkTypes.MetricType.Funnel ||
      props.entry.behavior.type === BookmarkTypes.MetricType.Retention ||
      props.entry.behavior.type === BookmarkTypes.MetricType.RetentionFrequency);

  return (
    <SelectReact
      className='subitem-select multi-attribution-type'
      elref='multiAttributionSelector'
      alwaysShowCaret
      label={print(getMultiAttributionLabel(props.entry, isMultiAttributionOpen))}
      title={isMultiAttributionOpen ? `` : print(getMultiAttributionTitle(props.entry))}
      open={isMultiAttributionOpen}
      theme={SelectTheme.Tertiary}
      size={ButtonSize.Small}
      onMenuChange={() => setIsMultiAttributionOpen(!isMultiAttributionOpen)}
      tooltip={{
        text: `${print(getMultiAttributionTitle(props.entry))}${props.extraLabel ? `\n${props.extraLabel}` : ``}.`,
        size: DisplaySize.Medium,
      }}
    >
      <div slot='body'>
        {isMultiAttributionOpen && (
          <MultiAttributionMenu
            onCancel={() => setIsMultiAttributionOpen(false)}
            onChange={(updatedMultiAttribution) => handleUpdateMultiAttribution(updatedMultiAttribution)}
            onDelete={() => handleUpdateMultiAttribution(null)}
            isSupportedMultiAttributionType={(type) =>
              isSupportedMultiAttributionTypeForEntry({
                entry: props.entry,
                multiAttributionType: type,
                metricsResource: metrics,
                behaviorsResource: behaviors,
              })
            }
            showMultiAttributionType={
              isRetentionOrFunnelBehavior
                ? (type) =>
                    [
                      MultiAttributionType.LastTouch,
                      MultiAttributionType.FirstTouch,
                      MultiAttributionType.Participation,
                    ].includes(type)
                : undefined
            }
            metricIndex={props.index}
            multiAttribution={multiAttribution || DefaultMultiAttribution}
            projectIsFreePlan={Boolean(props.sectionProps.projectIsFreePlan)}
          />
        )}
      </div>
    </SelectReact>
  );
}
